import React, { Component } from 'react';
import { connect } from 'react-redux';
import hashObject from 'object-hash';
import {
  reduce, indexOf, reject, findIndex, each, cloneDeep,
  find, concat, isArray, uniq, orderBy,
} from 'lodash';
import {
  Button, FormGroup, ControlLabel,
  FormControl, HelpBlock,
} from 'react-bootstrap';
import shortid from 'shortid';
import moment from 'moment';
import { Field, reduxForm, SubmissionError, formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import DatePicker from 'react-datepicker';
import ValueList from './value-list';
import DeleteButton from './delete-button';
import {
  addMeta, updateMeta,
  addMetaValue, removeMetaValue, updateMetaValue,
  reorderValues, deleteMeta,
} from './actions';
import styles from './styles.css';

const dataTypes = [
  { value: 'string', label: 'String', showDefaultField: true },
  { value: 'boolean', label: 'Check-box', showDefaultField: false },
  // { value: 'date', label: 'Date' },
  { value: 'picklist', label: 'Picklist' },
  // { value: 'multipicklist', label: 'Multi-picklist' },
  { value: 'currency', label: 'Currency', showDefaultField: true },
  { value: 'int', label: 'Integer', showDefaultField: true },
  // { value: 'double', label: 'Double', showDefaultField: true },
  // { value: 'percent', label: 'Percent', showDefaultField: true },
  { value: 'number', label: 'Number', showDefaultField: true },
];

const booleanDefaultValues = [
  { value: true, label: 'True' },
  { value: false, label: 'False' },
  { value: null, label: 'Null' },
];

const RadioGroup = ({
  label, name, options, input, meta: { touched, error }, disabled,
}) => {
  const validationState = touched && error ? 'error' : null;
  return (
    <FormGroup controlId={name} validationState={validationState}>
      <ControlLabel>{label}</ControlLabel>
      <div>
        {options.map((option) => {
          const checked = input.value === option.value || (option.value === null && input.value === '');
          return (
            <div className="radio-inline" key={option.value}>
              <label>
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={checked}
                  disabled={disabled}
                  onChange={(event) => {
                    let newValue = null;
                    if (event.target.checked) {
                      newValue = option.value;
                    }
                    return input.onChange(newValue);
                  }}
                />
                &nbsp;
                <code>{option.label}</code>
              </label>
            </div>
          );
        })}
      </div>
      {touched && error && <HelpBlock>{error}</HelpBlock>}
    </FormGroup>
  );
};
RadioGroup.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  input: PropTypes.shape({}),
  disabled: PropTypes.bool,
  meta: PropTypes.shape({}),
};
RadioGroup.defaultProps = {
  label: null,
  name: null,
  input: {},
  options: [],
  disabled: false,
  meta: {},
};

class CustomMetaForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      values: [...(props.meta.values || [])],
      deleting: false,
    };
    this.submit = this.submit.bind(this);
    this.renderField = this.renderField.bind(this);
    this.cancel = this.cancel.bind(this);
    this.renderDatePicker = this.renderDatePicker.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.requestNewValue = this.requestNewValue.bind(this);
    this.requestRemoveValue = this.requestRemoveValue.bind(this);
    this.requestUpdateValue = this.requestUpdateValue.bind(this);
    this.requestUpdateDefault = this.requestUpdateDefault.bind(this);
    this.requestUpdatePositions = this.requestUpdatePositions.bind(this);
    this.requestDeleteCustomMeta = this.requestDeleteCustomMeta.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isNew && nextProps.selectedDataType !== this.props.selectedDataType) {
      this.props.change('default_value', null);
    }
    const hashed1 = hashObject(this.props.meta);
    const hashed2 = hashObject(nextProps.meta);
    if (hashed1 !== hashed2) {
      this.props.initialize(nextProps.meta);
      this.setState({ values: [...(nextProps.meta.values || [])] });
    }
  }

  handleDateChange(date) {
    this.props.change('default_value', date ? date.format('YYYY-MM-DD') : null);
  }

  requestDeleteCustomMeta() {
    const { deleting } = this.state;
    return new Promise((resolve, rj) => {
      if (!deleting) {
        this.setState({ deleting: true });
        this.props.dispatch(deleteMeta(this.props.meta._id))
          .then(
            () => {
              this.props.onDeleteSuccess(this.props.meta);
              resolve(true);
            },
            (e) => {
              this.setState({ deleting: false });
              rj(e);
            },
          );
      } else {
        rj(new Error('deleting'));
      }
    });
  }

  requestNewValue(value) {
    const { isNew, meta } = this.props;
    return new Promise((resolve, rj) => {
      if (isNew) {
        const id = shortid.generate();
        const newValue = { _id: id, value: id, ...value };
        const { values } = this.state;
        this.setState({ values: concat(values, newValue) });
        resolve(newValue);
      } else {
        const params = { label: value.label };
        this.props.dispatch(addMetaValue(meta._id, params))
          .then(
            (newMeta) => {
              this.setState({ values: newMeta ? newMeta.values || [] : [] });
              resolve(newMeta);
            },
            (errors) => {
              rj(errors);
            },
          );
      }
    });
  }

  requestUpdatePositions(positions) {
    const { isNew, meta } = this.props;
    const { values } = this.state;
    const oldValues = cloneDeep(values);

    return new Promise((resolve, rj) => {
      each(positions, (val, position) => {
        const value = find(values, { value: val });
        if (value) {
          value.position = position + 1;
        }
      });
      const newValues = orderBy(values, ['position'], ['asc']);
      this.setState({ values: newValues });
      if (!isNew) {
        this.props.dispatch(reorderValues(meta._id, positions))
          .then(
            (newMeta) => {
              const rValues = newMeta ? newMeta.values || [] : [];
              this.setState({ values: rValues });
              resolve(rValues);
            },
            (errors) => {
              this.setState({ values: oldValues }); // reverse
              rj(errors);
            },
          );
      } else {
        resolve(newValues);
      }
    });
  }

  requestRemoveValue(value) {
    const { isNew, meta } = this.props;
    return new Promise((resolve, rj) => {
      if (isNew) {
        const id = value._id;
        const { values } = this.state;
        this.setState({ values: reject(values, { _id: id }) });
        resolve(true);
      } else {
        this.props.dispatch(removeMetaValue(meta._id, value.value))
          .then(
            (newMeta) => {
              this.setState({ values: newMeta ? newMeta.values || [] : [] });
              resolve(true);
            },
            (errors) => {
              rj(errors);
            },
          );
      }
    });
  }

  requestUpdateValue(value) {
    const { isNew, meta } = this.props;
    return new Promise((resolve, rj) => {
      if (isNew) {
        const id = value._id;
        const { values } = this.state;
        const idx = findIndex(values, { _id: id });
        if (idx >= 0) {
          const newValues = [...values];
          newValues[idx] = { ...values[idx], ...value };
          this.setState({ values: newValues });
          resolve(value);
        } else {
          resolve(null);
        }
      } else {
        this.props.dispatch(updateMetaValue(meta._id, value.value, { label: value.label }))
          .then(
            (newMeta) => {
              this.setState({ values: newMeta ? newMeta.values || [] : [] });
              resolve(true);
            },
            (errors) => {
              rj(errors);
            },
          );
      }
    });
  }

  requestUpdateDefault(id, isDefault) {
    const { selectedDataType, selectedDefaultValue } = this.props;
    return new Promise((resolve) => {
      const { values } = this.state;
      const idx = findIndex(values, { _id: id });
      if (idx >= 0) {
        const val = values[idx].value;
        let currentDefaults = null;
        let newDefault = null;
        if (selectedDataType === 'picklist') {
          currentDefaults = selectedDefaultValue;
          newDefault = currentDefaults === val ? null : val;
        } else if (selectedDataType === 'multipicklist') {
          currentDefaults = isArray(selectedDefaultValue) ? selectedDefaultValue : [selectedDefaultValue];
          if (isDefault) {
            newDefault = uniq(concat(currentDefaults, val));
          } else {
            // remove the val
            newDefault = reduce(currentDefaults, (items, currentDefault) => {
              if (currentDefault !== val) {
                items.push(currentDefault);
              }
              return items;
            }, []);
          }
        }
        this.props.change('default_value', newDefault);
      }
      resolve();
    });
  }

  submit(data) {
    const params = { ...data };
    const { values } = this.state;
    const { isNew, meta } = this.props;
    const id = meta._id;
    if (isNew) {
      if (params.data_type === 'picklist') {
        params.values = reduce(values, (items, val) => {
          items.push({
            label: val.label,
            default: params.default_value === val.value,
          });
          return items;
        }, []);
        delete params.default_value;
      } else if (params.data_type === 'multipicklist') {
        params.values = reduce(values, (items, val) => {
          items.push({
            label: val.label,
            default: indexOf(params.default_value, val.value) >= 0,
          });
          return items;
        }, []);
        delete params.default_value;
      }
    }
    const action = isNew ? addMeta(params) : updateMeta(id, params);
    return this.props.dispatch(action)
      .then(
        (m) => {
          this.props.onSuccess(m);
        },
        (error) => {
          throw new SubmissionError(error.data || error);
        },
      );
  }

  cancel() {
    this.props.onCancel();
  }
  renderField({
    input, label, componentClass, type, meta: { touched, error }, options, disabled,
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup validationState={validationState}>
        <ControlLabel>{label}</ControlLabel>
        {componentClass !== 'select' &&
          <FormControl
            {...input}
            componentClass={componentClass}
            placeholder={label}
            type={type}
            disabled={submitting || disabled}
          />
        }
        {componentClass === 'select' &&
          <FormControl {...input} componentClass={componentClass} placeholder={label} type={type} disabled={submitting || disabled}>
            <option />
            {options.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </FormControl>
        }
        <FormControl.Feedback />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderDatePicker({
    label, date, meta: { touched, error },
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup validationState={validationState} className={styles.datePicker}>
        <ControlLabel>{label}</ControlLabel>
        <DatePicker
          dateFormat="DD MMM YYYY"
          className={`form-control clickable ${styles.dateField}`}
          placeholderText="Click to select a date"
          selected={date}
          showYearDropdown
          disabled={submitting}
          scrollableYearDropdown
          isClearable
          popperPlacement="top-end"
          popperModifiers={{
            offset: {
              enabled: true,
              offset: '45px, 0px',
            },
          }}
          onChange={this.handleDateChange}
        />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderDefaultValueField() {
    const {
      selectedDataType, selectedDefaultValue,
      submitting, isNew,
    } = this.props;
    let showDefaultField = false;
    if (selectedDataType) {
      const dataType = find(dataTypes, { value: selectedDataType });
      if (dataType) {
        ({ showDefaultField } = dataType);
      }
    }
    const showBooleanDefaultField = selectedDataType === 'boolean';
    const showDateDefaultField = selectedDataType === 'date';
    if (showDefaultField) {
      return (
        <Field name="default_value" type="text" component={this.renderField} label="Default Value" />
      );
    }
    if (showBooleanDefaultField) {
      return (
        <Field
          name="default_value"
          label="Default Value"
          component={RadioGroup}
          disabled={submitting}
          options={booleanDefaultValues}
        />
      );
    }
    if (showDateDefaultField) {
      const dateMoment = selectedDefaultValue ? moment(selectedDefaultValue, 'YYYY-MM-DD') : null;
      const date = dateMoment && dateMoment.isValid() ? dateMoment : null;
      return (
        <Field
          name="default_value"
          label="Default Date"
          date={date}
          component={this.renderDatePicker}
        />
      );
    }

    if (selectedDataType === 'picklist' || selectedDataType === 'multipicklist') {
      return (
        <ValueList
          isMultiple={selectedDataType === 'multipicklist'}
          values={this.state.values}
          defaultValue={selectedDefaultValue}
          requestNewValue={this.requestNewValue}
          requestRemoveValue={this.requestRemoveValue}
          requestUpdateValue={this.requestUpdateValue}
          requestUpdateDefault={this.requestUpdateDefault}
          requestUpdatePositions={this.requestUpdatePositions}
          isNew={isNew}
          disabled={submitting}
        />
      );
    }

    return null;
  }

  render() {
    const {
      handleSubmit, submitting, isNew,
    } = this.props;
    let submitLabel = 'Save';
    if (isNew) {
      submitLabel = submitting ? 'Saving' : 'Save';
    } else {
      submitLabel = submitting ? 'Updating' : 'Update';
    }

    return (
      <div className={styles.form}>
        <form onSubmit={handleSubmit(this.submit)}>
          <Field name="label" type="text" component={this.renderField} label="Label" />
          <Field name="description" componentClass="textarea" component={this.renderField} label="Description" />

          <Field
            name="data_type"
            type="text"
            componentClass="select"
            options={dataTypes}
            component={this.renderField}
            label="Data Type"
            disabled={!isNew}
          />
          {this.renderDefaultValueField()}
          <div className="clearfix">
            <div className="pull-left">
              <Button disabled={submitting} bsStyle="link" onClick={this.cancel}>
                Cancel
              </Button>
            </div>
            <div className="pull-right">
              <Button type="submit" disabled={submitting} bsStyle="primary">
                {submitLabel}
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

const selector = formValueSelector('customMetaForm'); // <-- same as form name

function mapStateToProps(state, ownProps) {
  const initialValues = { ...ownProps.meta };
  return {
    initialValues,
    isNew: ownProps.meta && ownProps.meta._id === undefined,
    selectedDataType: selector(state, 'data_type'),
    selectedDefaultValue: selector(state, 'default_value'),
  };
}

CustomMetaForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  initialize: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
  change: PropTypes.func,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  validObjects: PropTypes.arrayOf(PropTypes.string),
  selectedDataType: PropTypes.string,
  selectedDefaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  meta: PropTypes.shape({
    _id: PropTypes.string,
    description: PropTypes.string,
    label: PropTypes.string,
    object_type: PropTypes.string,
    data_type: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};
CustomMetaForm.defaultProps = {
  validObjects: null,
  onSuccess: () => {},
  onCancel: () => {},
  handleSubmit: () => {},
  initialize: () => {},
  change: () => {},
  onDeleteSuccess: () => {},
  isNew: false,
  submitting: false,
  selectedDataType: null,
  selectedDefaultValue: null,
};

const CFForm = reduxForm({
  form: 'customMetaForm',
})(CustomMetaForm);

export default connect(mapStateToProps)(CFForm);
